'use client'

import { useMemo, useState } from 'react'
import { toast } from '@/lib/toast'
import { uploadFiles } from '@/utils/request'
import { postReviewApi } from '@/lib/commerce/services/review-api'

const MAX_IMAGE_BYTES = 5 * 1024 * 1024

export type ReviewFilterId = 'all' | '5' | 'photo'

export type ReviewsData = {
  counts: { rating: number; count: number }[]
  data: any[]
  totalCount: number
  average: number
  userReviews?: any[]
}

export function useProductReviews({ product, initialReviews }: { product: any; initialReviews?: ReviewsData }) {
  const reviews = initialReviews || { counts: [], data: [], totalCount: 0, average: 0, userReviews: [] }
  const userReviews = reviews.userReviews || []

  const [content, setContent] = useState('')
  const [rating, setRating] = useState(0)
  const [images, setImages] = useState<string[]>([])
  const [btnLoading, setBtnLoading] = useState(false)
  const [loadingImages, setLoadingImages] = useState(false)

  const deletedReview = userReviews.find((r: any) => r.status === 'deleted')
  const pendingReview = userReviews.find((r: any) => r.status === 'pending')
  const publishedReview = userReviews.find((r: any) => r.status === 'published')
  const hasAnyReview = userReviews.length > 0

  const totalCount = reviews.totalCount || 0
  const photoCount = useMemo(() => reviews.data.filter((r: any) => r.images && r.images.length > 0).length, [reviews.data])
  const fiveStarCount = useMemo(() => reviews.data.filter((r: any) => r.rating === 5).length, [reviews.data])

  const getFilteredReviews = (activeFilter: ReviewFilterId, sortNewest: boolean) => {
    const base = activeFilter === '5'
      ? reviews.data.filter((r: any) => r.rating === 5)
      : activeFilter === 'photo'
        ? reviews.data.filter((r: any) => r.images && r.images.length > 0)
        : reviews.data
    return sortNewest ? base : [...base].reverse()
  }

  const postReview = async () => {
    if (!rating || rating < 1 || rating > 5) {
      toast.error('Үнэлгээ өгнө үү')
      return false
    }
    setBtnLoading(true)
    try {
      const res = await postReviewApi({ productId: product.id, rating, comment: content, images, status: 'pending' })
      if (!res || res.error) {
        toast.error(res?.error || 'Алдаа гарлаа')
        return false
      }
      toast.success('Амжилттай үнэлгээ өглөө')
      setContent('')
      setRating(0)
      setImages([])
      window.location.reload()
      return true
    } catch (error) {
      console.error('Error posting review:', error)
      toast.error('Алдаа гарлаа')
      return false
    } finally {
      setBtnLoading(false)
    }
  }

  const uploadImages = async (files: FileList | File[] | null | undefined) => {
    if (!files || files.length < 1) return
    const validFiles: File[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!file.type.match(/image\/*/)) {
        toast.error('Зөвхөн зураг оруулна уу!')
        return
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error('Зурагны хэмжээ 5MB-с их байна!')
        return
      }
      validFiles.push(file)
    }
    if (validFiles.length === 0) return

    const formData = new FormData()
    validFiles.forEach(file => formData.append('images', file))
    setLoadingImages(true)
    try {
      const { data: uploadedImages, error } = await uploadFiles({ formData })
      if (error) {
        toast.error(error)
        return
      }
      if (!uploadedImages) {
        toast.error('Зураг хуулахад алдаа гарлаа')
        return
      }
      if (Array.isArray(uploadedImages)) {
        setImages(prev => [...prev, ...uploadedImages])
        toast.success('Зураг амжилттай хуулагдлаа')
      } else if (typeof uploadedImages === 'string') {
        setImages(prev => [...prev, uploadedImages])
        toast.success('Зураг амжилттай хуулагдлаа')
      }
    } catch (err) {
      console.error('Error uploading images:', err)
      toast.error('Зураг хуулахад алдаа гарлаа')
    } finally {
      setLoadingImages(false)
    }
  }

  const removeImage = (image: string) => {
    setImages(prev => prev.filter(img => img !== image))
    toast.success('Зураг амжилттай устгагдлаа')
  }

  return {
    reviews,
    userReviews,
    deletedReview, pendingReview, publishedReview, hasAnyReview,
    totalCount, photoCount, fiveStarCount,
    getFilteredReviews,
    content, setContent,
    rating, setRating,
    images, setImages,
    btnLoading,
    loadingImages,
    postReview,
    uploadImages,
    removeImage,
  }
}
